
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { ClientFileCategories, UploadedFile, Client, ClientType, FileCategory } from 'ah-api-gateways';
import FileUploader from 'ah-common-lib/src/common/components/upload/FileUploader.vue';
import { map, mergeMap, tap } from 'rxjs/operators';
import { RequestManager, waitForEntityChange } from 'ah-requests';
import { defaultAcceptanceFileTypes } from 'ah-common-lib/src/helpers/file';
import { useAuthStore } from '@/app/store/authModule';
import { useIndividualSettingsStore } from '@/app/store/individualSettingsModule';
import { of } from 'rxjs';

/**
 * Client Proof of Address and Identification documents uploader
 *
 * FIXME deprecated, use generic ClientDocumentsUploader instead
 *
 * Emits:
 *
 * - update:files (payload: UploadedFile[]): emmited when one of the files gets changed. Can be used with .sync
 */
@Component({
  components: {
    FileUploader,
  },
})
export default class DocumentsUploader extends Vue {
  /**
   * Files presented to client, if not set files will be loaded on mount
   *
   * Defaults to null
   */
  @Prop({ default: null }) files!: UploadedFile[] | null;

  /**
   * Whether to display individual documents
   *
   * Defaults to true
   */
  @Prop({ default: true }) showPersonal!: boolean | string;

  /**
   * Whether to display title above each document section
   *
   * Defaults to true
   */
  @Prop({ default: true }) showTitles!: boolean | string;

  /**
   * Whether to force individual documents only
   *
   * Defaults to false
   */
  @Prop({ default: false }) forceIndividual!: boolean | string;

  /**
   * Whether to display both section surrounded by card-boxes
   *
   * Defaults to false
   */
  @Prop({ default: false }) withBoxes!: boolean | string;

  /**
   * List of files to hide by FileCategory
   *
   * Defaults to empty string
   */
  @Prop({ default: () => [] }) hideFiles!: FileCategory[];

  /**
   * Whether to hide size limit and allowed files on bottom
   *
   * Defaults to true
   */
  @Prop({ default: true }) hideFooter!: boolean | string;

  /**
   * Possibly specifie the client that owns the displayed documents
   */
  @Prop({ required: false }) client?: Client;

  /**
   * Class to be applied to each file row
   *
   * Defaults to 'col col-12'
   */
  @Prop({ default: 'col col-12' }) fileClass!: string;

  $refs!: {
    proofOfAddress: FileUploader;
    idDocument: FileUploader;
  };

  private reqManager = new RequestManager();

  private innerFiles: UploadedFile[] = [];

  private defaultAcceptanceFileTypes = defaultAcceptanceFileTypes;

  created() {
    if (!this.files || this.files.length === 0) {
      this.reqManager
        .sameOrCancelAndNew(
          'getClientDocuments',
          this.$services.client.getDocuments(this.clientId, { errors: { silent: true } })
        )
        .subscribe((response) => {
          this.innerFiles = response;
          response.forEach((document) => this.individualSettingsStore.setClientDocument({ document }));
        });
    }
  }

  get authStore() {
    return useAuthStore();
  }

  get individualSettingsStore() {
    return useIndividualSettingsStore();
  }

  get photoId() {
    return this.innerFiles.find((document) => document.category === ClientFileCategories.PHOTO_ID) || null;
  }

  get proofOfAddress() {
    return this.innerFiles.find((document) => document.category === ClientFileCategories.PROOF_OF_ADDRESS) || null;
  }

  get proofOfFunds() {
    return this.innerFiles.find((document) => document.category === ClientFileCategories.PROOF_OF_FUNDS) || null;
  }

  get IncCertificate() {
    return this.innerFiles.find((document) => document.category === ClientFileCategories.INCORPORATED_DOCUMENT) || null;
  }

  get financialStatement() {
    return this.innerFiles.find((document) => document.category === ClientFileCategories.FINANCIAL_STATEMENT) || null;
  }

  get invoiceSample() {
    return this.innerFiles.find((document) => document.category === ClientFileCategories.SAMPLE_INVOICE) || null;
  }

  get anyPending() {
    return this.$refs.proofOfAddress?.isUploading || this.$refs.idDocument?.isUploading;
  }

  get shouldForceIndividual() {
    return this.forceIndividual !== false;
  }

  get isCompany() {
    return (this.client && this.client?.type === ClientType.COMPANY) || this.authStore.isCompanyClient;
  }

  get clientId() {
    return this.client?.id || this.authStore.loggedInIdentity!.client!.id;
  }

  get FileCategory() {
    return ClientFileCategories;
  }

  get fileCategories() {
    if (this.isCompany && !this.shouldForceIndividual) {
      return [
        ClientFileCategories.PROOF_OF_ADDRESS,
        ClientFileCategories.PHOTO_ID,
        ClientFileCategories.INCORPORATED_DOCUMENT,
        ClientFileCategories.FINANCIAL_STATEMENT,
        ClientFileCategories.SAMPLE_INVOICE,
      ];
    } else {
      if (this.isCompany) {
        return [ClientFileCategories.PROOF_OF_ADDRESS, ClientFileCategories.PHOTO_ID];
      } else {
        return [
          ClientFileCategories.PROOF_OF_ADDRESS,
          ClientFileCategories.PHOTO_ID,
          ClientFileCategories.PROOF_OF_FUNDS,
        ];
      }
    }
  }

  private shouldShow(category: FileCategory) {
    return !this.hideFiles.includes(category);
  }

  uploadFile(file: File, category: ClientFileCategories) {
    return this.$services.client.uploadDocument(this.clientId, category, file).pipe(
      mergeMap((file) => {
        if (file.finished) {
          return waitForEntityChange(
            () => this.$services.client.getDocuments(this.clientId),
            (docs) => {
              return !!docs.find((d) => d.id === file.file.id);
            }
          ).pipe(
            map((docs) => {
              const document = docs.find((d) => d.id === file.file.id);
              if (document) {
                const currFileIndex = this.innerFiles.findIndex((i) => i.category === document.category);
                currFileIndex > -1
                  ? this.innerFiles.splice(currFileIndex, 1, document)
                  : this.innerFiles.push(document);
              }
              return file;
            })
          );
        }
        return of(file);
      })
    );
  }

  deleteFile(file: UploadedFile) {
    return this.$services.client.deleteDocument(this.clientId, file.id).pipe(
      tap(() => {
        const currFileIndex = this.innerFiles.findIndex((i) => i.category === file.category);
        if (currFileIndex > -1) {
          this.innerFiles.splice(currFileIndex, 1);
        }

        this.individualSettingsStore.unsetClientDocument({ category: file.category as ClientFileCategories });
      })
    );
  }

  downloadFile(file: UploadedFile) {
    return this.$services.client.downloadSyncDocument(this.clientId, file);
  }

  uploadID(file: File) {
    return this.uploadFile(file, ClientFileCategories.PHOTO_ID);
  }

  uploadPOA(file: File) {
    return this.uploadFile(file, ClientFileCategories.PROOF_OF_ADDRESS);
  }

  uploadPOF(file: File) {
    return this.uploadFile(file, ClientFileCategories.PROOF_OF_FUNDS);
  }

  uploadINCC(file: File) {
    return this.uploadFile(file, ClientFileCategories.INCORPORATED_DOCUMENT);
  }

  uploadFS(file: File) {
    return this.uploadFile(file, ClientFileCategories.FINANCIAL_STATEMENT);
  }

  uploadSI(file: File) {
    return this.uploadFile(file, ClientFileCategories.SAMPLE_INVOICE);
  }

  @Watch('innerFiles')
  onInnerFilesChange() {
    this.$emit('update:files', this.innerFiles);
  }

  @Watch('files', { immediate: true })
  onFilesChange() {
    if (this.files !== null) {
      this.innerFiles = this.files;
    }
  }
}
